'use client';

import { useCallback, useEffect, useState, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { useWindowSize } from 'usehooks-ts';
import { cloneDeep } from 'lodash';
import { useMatomo } from '@datapunt/matomo-tracker-react';

import { RESOURCE_TYPE } from '@constants/ui';
import { LoginStatus } from '@constants/general';
import { DEFAULT_ALERT_CONFIG } from '@constants/alert';
import { PageTitle } from '@constants/matomo';

import AuthService from '@services/auth';
import ResourceService from '@services/resource';
import UiUtils from '@utils/ui';
import alertManager from '@classes/alert-manager';

import NavBar from '@components/nav-bar';
import Footer from '@components/footer';
import HomeFeature from '@components/home-feature';
import HomeCard from '@components/home-card';
import HomeStep from '@components/home-step';
import HomeResourceBlock from '@components/home-resource-block';
import Testimonial from '@components/testimonial';
import FaqSet from '@components/faq-set';
import SubscribeForm from '@components/subscribe-form';
import LogoBlock from '@components/logo-block';
import SpinnerButton from '@components/spinner-button';
import AlertConfirm from '@components/alert-confirm';

import LMCFLogo from '@images/testimonial_logo_lmcf.png';
import LMCFAvatar from '@images/testimonial_avatar_lmcf.jpg';
import GoodCycleLogo from '@images/testimonial_logo_good_cycle.png';
import GoodCycleAvatar from '@images/testimonial_avatar_good_cycle.png';
import ServantsLogo from '@images/testimonial_logo_servants.png';
import ServantsAvatar from '@images/testimonial_avatar_servants.jpg';
import WhiteBoxLogo from '@images/testimonial_logo_white_box.png';
import WhiteBoxAvatar from '@images/testimonial_avatar_white_box.jpg';

export default function HomePage() {
    const router = useRouter();

    /* Alert / Confirm Code */

    const [alertOpen, setAlertOpen] = useState(false);
    const [alertConfig, setAlertConfig] = useState(alertManager ? alertManager.getConfig() : DEFAULT_ALERT_CONFIG);

    const openAlert = (options) => {
        if (alertManager) {
            alertManager.openAlert(options);
            setAlertConfig(alertManager.getConfig());
            setAlertOpen(true);
        }
    }

    const onAlertClose = () => {
        if (alertManager) {
            setAlertOpen(false);
        }
    }

    const [loginStatus, setLoginStatus] = useState(LoginStatus.UNKNOWN);

    useEffect(() => {
        setLoginStatus(AuthService.isLoggedIn() ? LoginStatus.LOGGED_IN : LoginStatus.LOGGED_OUT);
    }, []);

    const [loginSpinning, setLoginSpinning] = useState(false);
    const [signUpSpinning, setSignUpSpinning] = useState(false);

    const getAvatar = (key) => {
        switch (key) {
            case "lmcf":
                return LMCFAvatar;
            case "goodCycle":
                return GoodCycleAvatar;
            case "servants":
                return ServantsAvatar;
            case "whiteBox":
                return WhiteBoxAvatar;
            default:
                return "";
        }
    }

    const getLogo = (key) => {
        switch (key) {
            case "lmcf":
                return LMCFLogo;
            case "goodCycle":
                return GoodCycleLogo;
            case "servants":
                return ServantsLogo;
            case "whiteBox":
                return WhiteBoxLogo;
            default:
                return "";
        }
    }

    const [articleState, setArticleState] = useState({
        news: {},
        learn: {},
        loaded: false
    });

    const subscribeFormRef = useRef(null);

    const loadArticles = useCallback(() => {
        ResourceService.getResources(true, RESOURCE_TYPE.All)
        .then(response => {
            let resources = [];
            response.data.resources.forEach(res => {
                let resource = cloneDeep(res);
                resource.thumbnail = UiUtils.adjustThumbnail(resource.thumbnail);
                resources.push(resource);
            });
            const newArticles = resources.filter(r => r.category === RESOURCE_TYPE.News);
            const newsIndex = Math.floor(Math.random() * newArticles.length);
            const learnArticles = resources.filter(r => r.category === RESOURCE_TYPE.Learn);
            const learnIndex = Math.floor(Math.random() * learnArticles.length);
            const newState = {
                news: newArticles[newsIndex],
                learn: learnArticles[learnIndex],
                loaded: true
            };
            setArticleState(newState);
        })
        .catch(err => {
            console.log(err);
            setArticleState({
                news: {},
                learn: {},
                loaded: false
            });
        });
    }, [setArticleState]);

    useEffect(() => {
        loadArticles();
    }, [loadArticles]);

    function doLogin() {
        setLoginSpinning(true);
        if (loginStatus === LoginStatus.LOGGED_IN) {
            router.push("/kit/home")
        } else {
            router.push("/login");
        }
    }

    function doSignUp() {
        setSignUpSpinning(true);
        if (loginStatus === LoginStatus.LOGGED_IN) {
            router.push("/kit/home")
        } else {
            router.push("/signup");
        }
    }

    /* Screen Responsive Code */

    const { width } = useWindowSize();

    const [isMobile, setIsMobile] = useState(false);
    const [isDesktop, setIsDesktop] = useState(false);

    useEffect(() => {
        const mobile = UiUtils.isMobile(width);
        setIsMobile(mobile);
        setIsDesktop(!mobile);
    }, [width, setIsMobile, setIsDesktop]);

    /* Testimonal Setting Code */

    const testimonials = [
        {
            quote: "Social enterprises have social or environmental impact at their centre. Measuring impact is critical for designing a highly effective enterprise and for engaging customers and investors. Seedkit is an outstanding resource designed specifically for social enterprises.",
            name: "Dr Catherine Brown OAM",
            role: "CEO, Lord Mayor's Charitable Foundation",
            key: "lmcf",
            logoWidth: 338,
            logoHeight: 251,
            logoAlt: "Logo of Lord Mayor's Charitable Foundation",
            avatarAlt: "Dr Catherine Brown OAM in a green jacket in front of a window with blinds"
        },
        {
            quote: "Social impact measurement is key to supporting internal decision-making, program monitoring and improvement, and informing organisational strategy. But the real importance of social impact measurement for social enterprises lies in creating the evidence to leverage and put to policymakers and key decision-makers.",
            name: "Hannah Figueroa",
            role: "Impact Evaluation Lead, Good Cycles",
            key: "goodCycle",
            logoWidth: 348,
            logoHeight: 120,
            logoAlt: "Logo of Good Cycle",
            avatarAlt: "Hannah Figueroa, side on, wearing a blue shirt"
        },
        {
            quote: "Although appetite for social impact measurement is growing, it's nearly impossible for lean social service organisations like ours to produce the increasingly complex data government, supporters, and partners want. Tools like Seedkit can measure everything in one place and automate our reporting, saving time and money for the work that matters most.",
            name: "Jayden Battey",
            role: "Communications & Engagement Manager, Servants Community Housing",
            key: "servants",
            logoWidth: 400,
            logoHeight: 140,
            logoAlt: "Logo of Servants Community Housing",
            avatarAlt: "Jayden Battey wearing a pale blue shirt in a suburban street"
        },
        {
            quote: "If we want to change the world we need the data behind the change that we want to see. I'm so excited about this work! Seedkit will game change the collection of data for the Social Enterprise Movement in Australia.",
            name: "Luke Terry",
            role: "CEO, White Box Enterprises",
            key: "whiteBox",
            logoWidth: 1744,
            logoHeight: 415,
            logoAlt: "Logo of White Box Enterprises",
            avatarAlt: "Luke Terry wearing a dark blue blazer over a light blue shirt"
        }
    ];

    const [testimonialIndex1, setTestimonialIndex1] = useState(-1);
    const [testimonialIndex2, setTestimonialIndex2] = useState(-1);

    useEffect(() => {
        let testIndexes = [0, 1, 2, 3];
        const t1 = Math.floor(Math.random() * testIndexes.length);
        setTestimonialIndex1(testIndexes[t1]);
        testIndexes.splice(t1, 1);
        const t2 = Math.floor(Math.random() * testIndexes.length);
        setTestimonialIndex2(testIndexes[t2]);
    }, [setTestimonialIndex1, setTestimonialIndex2]);

    /* Matomo Tracking Code */

    const { trackPageView } = useMatomo();

    useEffect(() => {
        trackPageView({
            documentTitle: PageTitle.HOME
        });
        // eslint-disable-next-line
    }, []);

    return(
        <>
            {isDesktop && (
                <div className="w-full h-full">
                    <div className="box-border w-full h-full py-6 flex flex-col items-stretch gap-10">
                        <header className="box-border flex flex-col items-stretch gap-10 py-10 px-8 mx-16 rounded-3xl bg-[url(/images/home_hero_bg.png)] bg-bottom-right bg-contain bg-no-repeat bg-light-green01">
                            <NavBar onAlertOpen={openAlert}/>
                            <div className="w-full px-6 py-10 border-box max-w-[1312px] mx-auto">
                                <div className="flex flex-col items-start gap-8 max-w-[699px]">
                                    <h1 className="font-vg-medium text-black text-64px leading-110">
                                        Track and communicate social enterprise impacts
                                    </h1>
                                    <p className="font-vg-regular text-black text-xl">
                                        Embark on your social impact journey with Seedkit.<br/> 
                                        Streamline your data tracking and magnify positive change.
                                    </p>
                                    <div className="flex justify-start items-center gap-8">
                                        {!AuthService.isLoggedIn() && (
                                            <SpinnerButton 
                                                variant="outline" 
                                                size="large" 
                                                label="Sign up"
                                                className="min-w-164px" 
                                                spinning={signUpSpinning}
                                                onClick={doSignUp}
                                            />
                                        )}
                                        <SpinnerButton 
                                            variant="solid" 
                                            size="large" 
                                            label={AuthService.isLoggedIn() ? "Go to your account" : "Login"} 
                                            rightIcon="ArrowRightIcon" 
                                            className="min-w-164px" 
                                            spinning={loginSpinning}
                                            onClick={doLogin}
                                        />
                                    </div>
                                </div>
                            </div>
                        </header>
                        <main className="box-border w-full flex flex-col items-stretch gap-10">
                            <div className="box-border flex flex-col items-stretch gap-10 bg-[url(/images/home_feature_card_bg.png)] bg-bottom-right bg-contain bg-no-repeat">
                                <div className="grid grid-cols-12 py-10 px-16 border-box max-w-[1440px] mx-auto">
                                    <div className="col-span-4">
                                        <h2 className="font-vg-medium text-black text-5xl leading-110">
                                            Social enterprise impact reporting, simplified
                                        </h2>
                                    </div>
                                    <div className="col-start-6 col-span-6 flex flex-col items-stretch gap-8">
                                        <div className="grid grid-cols-2 gap-6">
                                            <HomeFeature
                                                index="1" 
                                                icon="FaceSmileIcon"
                                                iconTitle="Smiley face icon"
                                                title="Easy to use"
                                                text="Streamline processes and simplify impact measurement with Seedkit's intuitive platform that works how you work - saving time and effort."
                                            />
                                            <HomeFeature 
                                                index="2" 
                                                icon="IdentificationIcon"
                                                iconTitle="ID card icon"
                                                title="Tailor your indicators"
                                                text="Customise impact metrics to fit your organisation's goals. You can select and adapt indicators that best reflect your unique social enterprise needs."
                                            />
                                        </div>
                                        <div className="grid grid-cols-2 gap-6">
                                            <HomeFeature
                                                index="3"  
                                                icon="ChartPieIcon"
                                                iconTitle="Pie chart icon"
                                                title="Visualise your data"
                                                text="Transform complex impact and business data with visualisation tools that help you compellingly present key trends."
                                            />
                                            <HomeFeature
                                                index="4"  
                                                icon="DocumentChartBarIcon"
                                                iconTitle="Bar graph icon"
                                                title="Create reports"
                                                text="Effortlessly generate reports showcasing your organisation's achievements, empowering you to effectively communicate your social enterprise's impact to stakeholders."
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="flex flex-col items-stretch px-16 py-10 gap-8 border-box max-w-[1440px] mx-auto">
                                    <div className="flex flex-col items-stretch gap-3">
                                        <h2 className="font-vg-medium text-black text-5xl">Measure and showcase impact with confidence</h2>
                                    </div>
                                    <div className="grid grid-cols-3 gap-6">
                                        <HomeCard
                                            colour="green"
                                            title="Sharing your business impact story"
                                            text="Seedkit helps document, measure, and communicate your organisational characteristics and impacts with a single source of truth, so you can 'enter once and report often' to your employees, board, funders and customers."
                                            image="/images/home_card_1_400.jpg"
                                            imageWidth={400}
                                            imageHeight={267}
                                            imageAlt="A young adult smiles and holds a bag of food while volunteers sort through boxes in the background."
                                        />
                                        <HomeCard
                                            colour="green"
                                            title="Strengthen impact measurement skills"
                                            text="Seedkit provides indicators, learning resources and user guidance to improve how you measure and communicate your social enterprise's impact and business success."
                                            image="/images/home_card_2_400.jpg"
                                            imageWidth={400}
                                            imageHeight={267}
                                            imageAlt="Four people around a desk sort through donated clothes and food."
                                        />
                                        <HomeCard
                                            colour="green"
                                            title="Building public knowledge"
                                            text="Seedkit is a powerful source of information about the social enterprise sector. Anonymised and aggregated data is analysed by experts to support strategy and policy advice that helps grow the sector."
                                            image="/images/home_card_3_400.jpg"
                                            imageWidth={400}
                                            imageHeight={267}
                                            imageAlt="Eight people standing in a circle stacking hands. They hold cleaning gloves and supplies."
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="flex flex-col items-stretch">
                                <div className=" bg-home-steps bg-bottom-right bg-contain bg-no-repeat bg-grey04">
                                    <div className="flex flex-col items-stretch px-16 py-20 gap-12 border-box max-w-[1440px] mx-auto">
                                        <h2 className="font-vg-medium text-black text-5xl leading-110">
                                            Seedkit works in 4 easy steps
                                        </h2>
                                        <div className="grid grid-cols-4 gap-5">
                                            <HomeStep
                                                number="01"
                                                title="You register"
                                                text="Set up an organisation account and register one or more social enterprises."
                                            />
                                            <HomeStep
                                                number="02"
                                                title="Choose indicators"
                                                text="Select or create indicators to manage your operations and report on how your social enterprise is changing the world."
                                            />
                                            <HomeStep
                                                number="03"
                                                title="Enter data"
                                                text="Define and submit data on indicators you want to measure regularly."
                                            />
                                            <HomeStep
                                                number="04"
                                                title="Communicate impact"
                                                text="Create and export reports from dashboards and share them with stakeholders."
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-grey03 bg-[url(/images/home_faq_bg.png)] bg-top-right bg-contain bg-no-repeat">
                                    <div className="flex flex-col items-stretch justify-center gap-12 p-16 border-box max-w-[1440px] mx-auto">
                                        <h2 className="font-vg-medium text-black text-[38px] leading-120 text-center">Frequently asked questions</h2>
                                        <div className="flex flex-col items-stretch gap-1">
                                            <FaqSet/>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="grid grid-cols-12 gap-6 py-16 px-16 max-w-[1440px] mx-auto">
                                <div className="col-span-5 col-start-1">
                                    {testimonialIndex1 > -1 && (
                                        <Testimonial
                                            quote={testimonials[testimonialIndex1].quote}
                                            name={testimonials[testimonialIndex1].name}
                                            role={testimonials[testimonialIndex1].role}
                                            logo={getLogo(testimonials[testimonialIndex1].key)}
                                            logoWidth={testimonials[testimonialIndex1].logoWidth}
                                            logoHeight={testimonials[testimonialIndex1].logoHeight}
                                            logoAlt={testimonials[testimonialIndex1].logoAlt}
                                            avatar={getAvatar(testimonials[testimonialIndex1].key)}
                                            avatarAlt={testimonials[testimonialIndex1].avatarAlt}
                                        />
                                    )}
                                </div>
                                <div className="col-span-5 col-start-7">
                                    {testimonialIndex2 > -1 && (
                                        <Testimonial
                                            quote={testimonials[testimonialIndex2].quote}
                                            name={testimonials[testimonialIndex2].name}
                                            role={testimonials[testimonialIndex2].role}
                                            logo={getLogo(testimonials[testimonialIndex2].key)}
                                            logoWidth={testimonials[testimonialIndex2].logoWidth}
                                            logoHeight={testimonials[testimonialIndex2].logoHeight}
                                            logoAlt={testimonials[testimonialIndex2].logoAlt}
                                            avatar={getAvatar(testimonials[testimonialIndex2].key)}
                                            avatarAlt={testimonials[testimonialIndex2].avatarAlt}
                                        />
                                    )}
                                </div>
                            </div>
                            {articleState.loaded === true && (
                                <div className="flex flex-col items-stretch pt-10 gap-12 px-16 max-w-[1440px] mx-auto">
                                    <div className="flex flex-col items-start gap-4">
                                        <h2 className="font-vg-medium text-black text-[38px] leading-120">
                                            Impact measurement news and resources
                                        </h2>
                                        <p className="font-vg-book text-black text-xl leading-130">
                                            Access product updates, learning resources, events and more.
                                        </p>
                                    </div>
                                    <div className="grid grid-cols-2 gap-6">
                                        <HomeResourceBlock
                                            type={articleState.news.resourceType}
                                            tags={[{ text: "News", color: "green" }]}
                                            image={{
                                                url: articleState.news.thumbnail.images.large.url,
                                                alt: articleState.news.thumbnail.alt,
                                                width: articleState.news.thumbnail.images.large.width,
                                                height: articleState.news.thumbnail.images.large.width
                                            }}
                                            title={articleState.news.title}
                                            text={articleState.news.lead}
                                            link={`/resources/${articleState.news.key}`}
                                        />
                                        <HomeResourceBlock
                                            type={articleState.learn.resourceType}
                                            tags={[{ text: "Learn", color: "green" }]}
                                            image={{
                                                url: articleState.learn.thumbnail.images.large.url,
                                                alt: articleState.learn.thumbnail.alt,
                                                width: articleState.learn.thumbnail.images.large.width,
                                                height: articleState.learn.thumbnail.images.large.height
                                            }}
                                            imageAlt={articleState.learn.thumbnail.alt}
                                            title={articleState.learn.title}
                                            text={articleState.learn.lead}
                                            link={`/resources/${articleState.learn.key}`}
                                        />
                                    </div>
                                </div>
                            )}
                            <div ref={subscribeFormRef} id="subscribe-form" className="pt-10 px-16">
                                <SubscribeForm/>
                            </div>
                            <LogoBlock/>
                        </main>
                        <Footer className="mx-16"/>
                    </div>
                    <AlertConfirm
                        open={alertOpen}
                        type={alertConfig.type}
                        options={alertConfig.options}
                        onOkClick={alertConfig.okHandler}
                        onCancelClick={alertConfig.cancelHandler}
                        onClose={() => onAlertClose()}
                    />
                </div>
            )}
            {isMobile && (
                <div className="w-full h-full">
                    <div className="box-border w-full h-full py-6 flex flex-col items-stretch gap-8">
                        <header className="box-border flex flex-col gap-8 pt-6 px-6 pb-12 rounded-3xl bg-[url(/images/home_hero_mobile_bg.png)] bg-bottom-right bg-contain bg-no-repeat bg-light-green01 mx-5">
                            <NavBar onAlertOpen={openAlert}/>
                            <div className="flex flex-col items-start gap-6">
                                <h1 className="font-vg-medium text-black text-4xl">
                                    Track and communicate social enterprise impacts
                                </h1>
                                <p className="font-vg-book text-black text-base">
                                    Embark on your social impact journey with Seedkit. 
                                    Streamline your data tracking and magnify positive change.
                                </p>
                                {!AuthService.isLoggedIn() && (
                                    <SpinnerButton 
                                        variant="outline" 
                                        size="large" 
                                        label="Sign up" 
                                        className="w-full"
                                        spinning={signUpSpinning}
                                        onClick={doSignUp}
                                    />
                                )}
                                <SpinnerButton 
                                    variant="solid" 
                                    size="large" 
                                    className="w-full" 
                                    label={AuthService.isLoggedIn() ? "Go to your account" : "Login"} 
                                    rightIcon="ArrowRightIcon" 
                                    spinning={loginSpinning}
                                    onClick={doLogin}
                                />
                            </div>
                        </header>
                        <main className="box-border w-full flex flex-col items-stretch gap-8">
                            <div className="box-border flex flex-col items-stretch gap-8 py-8 px-6 mx-5">
                                <h2 className="font-vg-medium text-black text-4xl">Social enterprise impact reporting, simplified</h2>
                                <div className="flex flex-col items-stretch gap-6">
                                    <HomeFeature
                                        index="1"  
                                        icon="FaceSmileIcon"
                                        iconTitle="Smiley face icon"
                                        title="Easy to use"
                                        text="Streamline processes and simplify impact measurement with Seedkit's intuitive platform that works how you work - saving time and effort."
                                    />
                                    <HomeFeature 
                                        index="2"
                                        icon="IdentificationIcon"
                                        iconTitle="ID card icon"
                                        title="Tailor your indicators"
                                        text="Customise impact metrics to fit your organisation's goals. You can select and adapt indicators that best reflect your unique social enterprise needs."
                                    />
                                    <HomeFeature
                                        index="3"  
                                        icon="ChartPieIcon"
                                        iconTitle="Pie chart icon"
                                        title="Visualise your data"
                                        text="Transform complex impact and business data with visualisation tools that help you compellingly present key trends."
                                    />
                                    <HomeFeature
                                        index="4"  
                                        icon="DocumentChartBarIcon"
                                        iconTitle="Bar graph icon"
                                        title="Create reports"
                                        text="Effortlessly generate reports showcasing your organisation's achievements, empowering you to effectively communicate your social enterprise's impact to stakeholders."
                                    />
                                </div>
                            </div>
                            <div className="flex flex-col items-stretch pt-8 gap-8">
                                <h2 className="font-vg-medium text-black text-4xl px-5">Measure and showcase impact with confidence</h2>
                                <div className="flex flex-col items-stretch gap-6 px-5">
                                    <HomeCard
                                        colour="green"
                                        title="Sharing your business impact story"
                                        text="Seedkit helps you document, measure, and communicate your organisational characteristics and impacts with a single source of truth, so you can 'enter once and report often' to your employees, board, funders and customers."
                                        image="/images/home_card_1.jpg"
                                        imageWidth={1024}
                                        imageHeight={750}
                                        imageAlt="A young adult is smiling and holding a bag of food. Behind them, four people in volunteering shirts sort through boxes."
                                    />
                                    <HomeCard
                                        colour="green"
                                        title="Strengthen impact measurement skills"
                                        text="Seedkit provides indicators, learning resources and user guidance to improve how you measure and communicate your social enterprise's impact and business success."
                                        image="/images/home_card_2.jpg"
                                        imageWidth={1024}
                                        imageHeight={750}
                                        imageAlt="Four people working together around a desk to sort donated clothes and food."
                                    />
                                    <HomeCard
                                        colour="green"
                                        title="Building public knowledge"
                                        text="Seedkit is a powerful source of information about the social enterprise sector. Anonymised and aggregated data are analysed by experts to support strategy and policy advice that helps grow the sector."
                                        image="/images/home_card_3.jpg"
                                        imageWidth={1024}
                                        imageHeight={750}
                                        imageAlt="Eight people are standing in a circle outside, stacking their hands. They hold cleaning gloves and supplies."
                                    />
                                </div>
                                <div className="flex flex-col items-stretch">
                                    <div className="flex flex-col items-stretch gap-[38px] bg-[url(/images/home_steps_mobile_bg.png)] bg-bottom-right bg-no-repeat px-5">
                                        <div className="flex flex-col items-start gap-4">
                                            <h2 className="font-vg-medium text-black text-4xl" dangerouslySetInnerHTML={{__html: "Seedkit works in<br/>4 easy steps"}}></h2>
                                        </div>
                                        <div className="flex flex-col items-stretch gap-8 mb-8">
                                            <HomeStep
                                                number="01"
                                                title="You register"
                                                text="Set up an organisation account and register one or more social enterprises."
                                            />
                                            <HomeStep
                                                number="02"
                                                title="Choose indicators"
                                                text="Select or create indicators to manage your operations and report on how your social enterprise is changing the world."
                                            />
                                            <HomeStep
                                                number="03"
                                                title="Enter data"
                                                text="Define and submit data on indicators you want to measure regularly."
                                            />
                                            <HomeStep
                                                number="04"
                                                title="Communicate impact"
                                                text="Create and export reports from dashboards and share them with stakeholders."
                                            />
                                        </div>
                                    </div>
                                    <div className="flex flex-col items-stretch gap-8 bg-grey03 bg-[url(/images/home_faq_mobile_bg.png)] bg-bottom-right bg-no-repeat px-5 py-16">
                                        <h3 className="font-vg-medium text-black text-[28px] text-center">Frequently asked questions</h3>
                                        <div className="flex flex-col items-stretch gap-4">
                                            <FaqSet/>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="flex flex-col items-stretch gap-6 px-5">
                                <Testimonial
                                    quote={testimonials[testimonialIndex1].quote}
                                    name={testimonials[testimonialIndex1].name}
                                    role={testimonials[testimonialIndex1].role}
                                    logo={getLogo(testimonials[testimonialIndex1].key)}
                                    logoWidth={testimonials[testimonialIndex1].logoWidth}
                                    logoHeight={testimonials[testimonialIndex1].logoHeight}
                                    logoAlt={testimonials[testimonialIndex1].logoAlt}
                                    avatar={getAvatar(testimonials[testimonialIndex1].key)}
                                    avatarAlt={testimonials[testimonialIndex1].avatarAlt}
                                />
                                <Testimonial
                                    quote={testimonials[testimonialIndex2].quote}
                                    name={testimonials[testimonialIndex2].name}
                                    role={testimonials[testimonialIndex2].role}
                                    logo={getLogo(testimonials[testimonialIndex2].key)}
                                    logoWidth={testimonials[testimonialIndex2].logoWidth}
                                    logoHeight={testimonials[testimonialIndex2].logoHeight}
                                    logoAlt={testimonials[testimonialIndex2].logoAlt}
                                    avatar={getAvatar(testimonials[testimonialIndex2].key)}
                                    avatarAlt={testimonials[testimonialIndex2].avatarAlt}
                                />
                            </div>
                            {articleState.loaded === true && (
                                <div className="flex flex-col items-stretch gap-6 pt-8 px-5">
                                    <div className="flex flex-col items-stretch gap-3">
                                        <h3 className="font-vg-medium text-black text-[28px]">Impact measurement news and resources</h3>
                                        <p className="font-vg-book text-black text-base leading-130">Access product updates, learning resources, events and more.</p>
                                    </div>
                                    <HomeResourceBlock
                                        type={articleState.news.resourceType}
                                        tags={[{ text: "News", color: "green" }]}
                                        image={articleState.news.heroImage.largeUrl}
                                        title={articleState.news.title}
                                        text={articleState.news.lead}
                                        link={`/resources/${articleState.news.key}`}
                                    />
                                    <HomeResourceBlock
                                        type={articleState.news.resourceType}
                                        tags={[{ text: "Learn", color: "green" }]}
                                        image={articleState.learn.heroImage.largeUrl}
                                        title={articleState.learn.title}
                                        text={articleState.learn.lead}
                                        link={`/resources/${articleState.learn.key}`}
                                    />
                                </div>
                            )}
                            <div ref={subscribeFormRef} id="subscribe-form" className="pt-8">
                                <SubscribeForm/>
                            </div>
                            <LogoBlock/>
                        </main>
                        <Footer className="mx-5"/>
                    </div>
                    <AlertConfirm
                        open={alertOpen}
                        type={alertConfig.type}
                        options={alertConfig.options}
                        onOkClick={alertConfig.okHandler}
                        onCancelClick={alertConfig.cancelHandler}
                        onClose={() => onAlertClose()}
                    />
                </div>
            )}
        </>
    );
}